import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Modal, Panel, Segments, Empty, useFlash, useModal } from '../../ui/kit.js';
import { useBuilding, RESIDENT, PULSE_MS, isoDay, printDate, clock } from '../../state/building.js';
import { Icon, LockGlyph } from '../../ui/access/glyphs.js';
import { EventRow, Toast } from '../../ui/access/parts.js';
import { PseudoQr } from '../../ui/access/qr.js';
import { CODE_TONE, codeStatus, newCode, residentBlock, residentHours, spaced } from '../../ui/access/model.js';

import Lobby from '../../media/images/modules/lobby-reception.jpg';
import Garage from '../../media/images/modules/parking-garage.jpg';
import Packages from '../../media/images/packages-screen/package-6.jpg';
import Gym from '../../media/images/modules/fitness-center.jpg';
import Pool from '../../media/images/modules/area-pool-deck.jpg';
import Skyline from '../../media/images/modules/building-skyline.jpg';

const EASE = [0.16, 1, 0.3, 1];

/* The resident's doors, in the order they meet them coming home. */
const TILES = [
    { door: 'main', image: Lobby },
    { door: 'garage-gate', image: Garage },
    { door: 'package', image: Packages },
    { door: 'fitness', image: Gym },
    { door: 'pool', image: Pool },
    { door: 'rooftop', image: Skyline }
];

const WINDOWS = [
    'All day',
    'Morning, 8:00 AM to 12:00 PM',
    'Afternoon, 12:00 PM to 5:00 PM',
    'Evening, 5:00 PM to 10:00 PM'
];

const USES = [{ value: 'Once', label: 'One entry' }, { value: 'All day', label: 'Unlimited that day' }];

/* How long the tile spends "talking to the reader" before it opens.
   Long enough to read as a handshake, short enough not to feel slow. */
const HANDSHAKE_MS = 750;

/* One door the resident can open from their phone. A tap fills the
   ring (the handshake), springs the shackle and lights the tile while
   the door is held open, drains the ring as the seconds run out, then
   relocks. The unlock itself goes to the shared feed the moment the
   door opens, so the front desk sees it as it happens. */
function DoorTile({ door, image, hours, blocked, onUnlock, index }) {
    const reduced = useReducedMotion();
    const [phase, setPhase] = useState('idle');
    const timers = useRef([]);

    useEffect(() => () => timers.current.forEach(clearTimeout), []);

    const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    const handshake = reduced ? 0 : HANDSHAKE_MS;
    const gate = door.id === 'garage-gate';

    const press = () => {
        if (blocked || phase === 'pending' || phase === 'open') return;
        timers.current.forEach(clearTimeout);
        timers.current = [];
        setPhase('pending');
        later(() => {
            setPhase('open');
            onUnlock(door);
            if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(30);
        }, handshake);
        later(() => setPhase('relocked'), handshake + PULSE_MS);
        later(() => setPhase('idle'), handshake + PULSE_MS + 2200);
    };

    const status = blocked || {
        idle: gate ? 'Tap to open the gate' : 'Tap to unlock',
        pending: 'Connecting to the reader',
        open: gate ? 'Gate open for 5 seconds' : 'Unlocked, door open for 5 seconds',
        relocked: 'Locked again'
    }[phase];

    /* The ring fills during the handshake, then drains across the five
       seconds the door is open. At rest it is hidden, not just empty: a
       round cap on a zero-length stroke still draws a dot. */
    const ring = {
        idle: { pathLength: 0, opacity: 0, transition: { duration: 0.3 } },
        pending: { pathLength: 1, opacity: 1, transition: { duration: handshake / 1000, ease: EASE } },
        open: { pathLength: 0, opacity: 1, transition: { duration: reduced ? 0 : PULSE_MS / 1000, ease: 'linear' } },
        relocked: { pathLength: 0, opacity: 0, transition: { duration: 0.2 } }
    }[phase];

    return (
        <motion.button
            type="button"
            className="acc-tile"
            data-phase={phase}
            data-blocked={blocked ? 'true' : undefined}
            onClick={press}
            disabled={!!blocked}
            aria-label={`${door.name}. ${status}`}
            initial={reduced ? false : { opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: Math.min(0.1 + index * 0.06, 0.6), ease: EASE }}
            whileTap={reduced || blocked ? undefined : { scale: 0.97 }}
        >
            <img className="acc-tile__photo" src={image} alt="" />
            <span className="acc-tile__shade" aria-hidden="true" />
            <span className="acc-tile__glow" aria-hidden="true" />

            <span className="acc-tile__head">
                <span className="acc-tile__name">{door.name}</span>
                <span className="acc-tile__hours">{hours}</span>
            </span>

            <span className="acc-tile__lock" aria-hidden="true">
                <svg className="acc-tile__ring" viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r="36" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
                    <motion.circle
                        cx="40" cy="40" r="36"
                        fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round"
                        transform="rotate(-90 40 40)"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: ring.pathLength, opacity: ring.opacity }}
                        transition={ring.transition}
                    />
                </svg>
                <LockGlyph open={phase === 'open'} size={30} />
            </span>

            <span className="acc-tile__foot">
                <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                        key={status}
                        className="acc-tile__status"
                        initial={reduced ? false : { opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.28, ease: EASE }}
                    >
                        {blocked ? <Icon name="shield" size={14} /> : null}
                        {status}
                    </motion.span>
                </AnimatePresence>
            </span>
        </motion.button>
    );
}

/* The share sheet for one guest code: the PIN to read out, a QR-like
   picture to forward, and the two things a resident does next. */
function CodeSheet({ code }) {
    const status = codeStatus(code);

    return (
        <div className="acc-codesheet">
            <PseudoQr value={code.code} size={176} />
            <div className="acc-codesheet__info">
                <span className="form-label">Entry code</span>
                <div className="acc-pin" aria-label={`Code ${code.code.split('').join(' ')}`}>
                    {code.code.split('').map((digit, i) => (
                        <motion.span
                            key={`${code.code}-${i}`}
                            className="acc-pin__digit"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.05 * i, ease: EASE }}
                        >
                            {digit}
                        </motion.span>
                    ))}
                </div>
                <dl className="acc-specs">
                    <div><dt>Guest</dt><dd>{code.guest}</dd></div>
                    <div><dt>Status</dt><dd><Chip tone={CODE_TONE[status]}>{status}</Chip></dd></div>
                    <div><dt>Day</dt><dd>{code.date}</dd></div>
                    <div><dt>Window</dt><dd>{code.window}</dd></div>
                    <div><dt>Uses</dt><dd>{code.uses === 'Once' ? 'One entry' : 'Unlimited that day'}</dd></div>
                    <div><dt>Doors</dt><dd>Main entrance, garage gate</dd></div>
                </dl>
                <p className="acc-faint">Your guest enters the code on the keypad at the main entrance or holds the picture to the reader.</p>
            </div>
        </div>
    );
}

/* Door Access: the resident's mobile key. Doors open from here, guests
   get a code from here, and a lost fob is switched off from here, all
   on the same records the front desk works from. */
function DoorAccessScreen() {
    const {
        doors, credentials, guestCodes, accessEvents, schedules, holidayHours, lockdown,
        add, patch, logAccess, pulseDoor
    } = useBuilding();

    const [flash, notify] = useFlash();
    const sheet = useModal();
    const lost = useModal();
    const [guest, setGuest] = useState({ name: '', date: isoDay(0), window: WINDOWS[0], uses: 'Once' });

    const mobile = credentials.find(c => c.name === RESIDENT.name && c.medium === 'Mobile key');
    const fob = credentials.find(c => c.name === RESIDENT.name && c.medium === 'Fob');
    const mine = guestCodes.filter(c => c.unit === RESIDENT.unit);
    /* Entries only: the doors that opened for the resident or their
       guests, not the system's notes about their credentials. */
    const history = accessEvents
        .filter(e => e.kind === 'granted' && e.door && (e.who === RESIDENT.name || e.guestOf === RESIDENT.unit))
        .slice(0, 8);

    /* The sheet keeps its code while it animates closed. */
    const sheetCode = useRef(null);
    if (sheet.isOpen) sheetCode.current = sheet.item;
    const shownCode = guestCodes.find(c => c.code === sheetCode.current) || null;

    const unlock = door => {
        pulseDoor(door.id, {
            kind: 'granted', tone: 'ok', label: 'Granted', title: 'Mobile key unlock',
            who: RESIDENT.name, via: 'Mobile key', unit: RESIDENT.unit
        });
    };

    const create = event => {
        event.preventDefault();
        if (!guest.name.trim()) return notify('Add your guest\'s name first.');

        const code = newCode(guestCodes.map(c => c.code));
        add('guestCodes', {
            code,
            guest: guest.name.trim(),
            unit: RESIDENT.unit,
            dateValue: guest.date || isoDay(0),
            date: printDate(guest.date),
            window: guest.window,
            uses: guest.uses,
            status: 'Active',
            created: clock()
        });
        logAccess({ kind: 'system', tone: 'info', label: 'Visitor', title: 'Visitor code created', who: guest.name.trim(), via: `Code ${code}`, note: `Guest of ${RESIDENT.unit}` });
        setGuest(g => ({ ...g, name: '' }));
        sheet.open(code);
    };

    const share = code => {
        patch('guestCodes', code.id, { sharedAt: clock() });
        notify(`Code sent to ${code.guest} by text.`);
    };

    const revoke = (code, inSheet) => {
        patch('guestCodes', code.id, { status: 'Revoked' });
        logAccess({ kind: 'system', tone: 'warn', label: 'Visitor', title: 'Visitor code revoked', who: code.guest, via: `Code ${code.code}`, note: `Guest of ${RESIDENT.unit}` });
        if (inSheet) sheet.close();
        notify(`Code ${spaced(code.code)} revoked. It no longer opens any door.`);
    };

    const reportLost = () => {
        lost.close();
        if (!fob) return;
        patch('credentials', fob.id, { status: 'Lost' });
        logAccess({ kind: 'system', tone: 'alert', label: 'Credential', title: 'Fob reported lost', who: RESIDENT.name, via: `Fob ${fob.number}`, note: 'Deactivated from the resident app' });
        notify('Replacement fob ready at the front desk.');
    };

    const keyChip = lockdown
        ? <Chip tone="alert">Paused for lockdown</Chip>
        : mobile && mobile.status === 'Active'
            ? <Chip tone="ok">Mobile key ready</Chip>
            : <Chip tone="warn">Mobile key suspended</Chip>;

    return (
        <div className="screen-content acc-screen">
            <ScreenHead eyebrow="Door access" title="Your keys" aside={keyChip} />
            <Toast>{flash}</Toast>

            <AnimatePresence initial={false}>
                {lockdown && (
                    <motion.div
                        className="acc-notice"
                        role="note"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.45, ease: EASE }}
                    >
                        <div className="acc-notice__inner">
                            <span className="acc-notice__icon"><Icon name="shield" size={22} /></span>
                            <div>
                                <strong>Doors are locked down by the front desk</strong>
                                <p>Opening doors from your phone is paused until the front desk ends the lockdown. Need help? Call the desk on extension 100, or 911 in an emergency.</p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className={`acc-tiles${lockdown ? ' acc-tiles--lockdown' : ''}`}>
                {TILES.map((tile, i) => {
                    const door = doors.find(d => d.id === tile.door);
                    if (!door) return null;
                    return (
                        <DoorTile
                            key={door.id}
                            index={i}
                            door={door}
                            image={tile.image}
                            hours={residentHours(door, schedules, holidayHours)}
                            blocked={residentBlock(door, { lockdown, schedules, credential: mobile })}
                            onUnlock={unlock}
                        />
                    );
                })}
            </div>
            <p className="acc-hint">
                <Icon name="phone" size={14} /> Or hold your phone near any reader. Every unlock is logged with the front desk.
            </p>

            <div className="split section-gap">
                <Panel as="form" className="panel--accent" onSubmit={create}>
                    <h4>Give a guest a code</h4>
                    <p>A six-digit code that opens the main entrance and garage gate for one visit, or for the day.</p>
                    <div className="form">
                        <Field label="Guest name" wide>
                            <input className="form-control" type="text" value={guest.name} onChange={e => setGuest(g => ({ ...g, name: e.target.value }))} placeholder="e.g. Jordan Blake" />
                        </Field>
                        <Field label="Day">
                            <input className="form-control" type="date" min={isoDay(0)} value={guest.date} onChange={e => setGuest(g => ({ ...g, date: e.target.value }))} />
                        </Field>
                        <Field label="Time window">
                            <select className="form-control" value={guest.window} onChange={e => setGuest(g => ({ ...g, window: e.target.value }))}>
                                {WINDOWS.map(w => <option key={w}>{w}</option>)}
                            </select>
                        </Field>
                        <div className="field field--wide">
                            <span className="form-label">Uses</span>
                            <Segments label="How many entries" options={USES} value={guest.uses} onChange={uses => setGuest(g => ({ ...g, uses }))} />
                        </div>
                        <button type="submit" className="btn btn-primary"><Icon name="code" size={16} /> Create code</button>
                    </div>
                </Panel>

                <div className="stack">
                    <h4>Your guest codes</h4>
                    {mine.length === 0 ? <Empty>No guest codes yet.</Empty> : (
                        <ul className="rows">
                            {mine.map(code => {
                                const status = codeStatus(code);
                                return (
                                    <li className="row acc-coderow" key={code.id}>
                                        <button type="button" className="acc-coderow__qr" onClick={() => sheet.open(code.code)} aria-label={`Show code for ${code.guest}`}>
                                            <PseudoQr value={code.code} size={44} label="" />
                                        </button>
                                        <div className="row__main">
                                            <span className="row__title">{code.guest}</span>
                                            <span className="row__meta">
                                                <span className="acc-num acc-code">{spaced(code.code)}</span> · {code.date} · {code.window} · {code.uses === 'Once' ? 'One entry' : 'All day'}
                                                {code.usedAt ? ` · used ${code.usedAt}` : ''}
                                                {code.sharedAt && status === 'Active' ? ` · sent ${code.sharedAt}` : ''}
                                            </span>
                                        </div>
                                        <div className="row__end">
                                            <Chip tone={CODE_TONE[status]}>{status}</Chip>
                                            {status === 'Active' && (
                                                <>
                                                    <button type="button" className="btn btn-outline-light btn-sm" onClick={() => share(code)}><Icon name="share" size={14} /> Share</button>
                                                    <button type="button" className="btn btn-outline-light btn-sm" onClick={() => revoke(code)}>Revoke</button>
                                                </>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            <div className="split section-gap">
                <Panel className={`acc-fob${fob && fob.status !== 'Active' ? ' acc-fob--off' : ''}`}>
                    <div className="acc-fob__art" aria-hidden="true">
                        <span className="acc-fob__ring" />
                        <span className="acc-fob__body">
                            <Icon name="key" size={18} />
                            <span className="acc-num">{fob ? fob.number : '----'}</span>
                        </span>
                    </div>
                    <div className="acc-fob__text">
                        <h4>Lost your fob?</h4>
                        {fob && fob.status === 'Active' && (
                            <p>Switch off fob <span className="acc-num">{fob.number}</span> now and it stops opening every door at once. Your mobile key keeps working.</p>
                        )}
                        {fob && fob.status !== 'Active' && (
                            <p>Fob <span className="acc-num">{fob.number}</span> is switched off. A replacement is ready at the front desk; bring a photo ID. Your mobile key still works.</p>
                        )}
                        <div className="acc-fob__foot">
                            {fob && <Chip tone={fob.status === 'Active' ? 'ok' : 'alert'}>{fob.status === 'Active' ? 'Fob active' : 'Fob deactivated'}</Chip>}
                            {fob && fob.status === 'Active' && (
                                <button type="button" className="btn btn-outline-light btn-sm" onClick={() => lost.open(true)}>Report lost fob</button>
                            )}
                        </div>
                    </div>
                </Panel>

                <div className="stack">
                    <h4>Your recent entries</h4>
                    {history.length === 0 ? <Empty>No entries yet.</Empty> : (
                        <ul className="acc-list acc-list--boxed">
                            <AnimatePresence initial={false}>
                                {history.map(e => (
                                    <motion.li
                                        key={e.id}
                                        layout="position"
                                        initial={{ opacity: 0, y: -8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.45, ease: EASE }}
                                    >
                                        <EventRow event={e} when="day" />
                                    </motion.li>
                                ))}
                            </AnimatePresence>
                        </ul>
                    )}
                </div>
            </div>

            <Modal
                open={sheet.isOpen && !!shownCode}
                onClose={sheet.close}
                size="lg"
                eyebrow="Guest access code"
                title={shownCode ? `Code for ${shownCode.guest}` : ''}
                actions={shownCode && (
                    <>
                        {codeStatus(shownCode) === 'Active' && (
                            <button type="button" className="btn btn-outline-light" onClick={() => revoke(shownCode, true)}>Revoke</button>
                        )}
                        <button type="button" className="btn btn-outline-light" onClick={sheet.close}>Done</button>
                        {codeStatus(shownCode) === 'Active' && (
                            <button type="button" className="btn btn-primary" onClick={() => share(shownCode)}><Icon name="share" size={16} /> Share by text</button>
                        )}
                    </>
                )}
            >
                {shownCode && <CodeSheet code={shownCode} />}
            </Modal>

            <Modal
                open={lost.isOpen}
                onClose={lost.close}
                eyebrow="Lost fob"
                title={fob ? `Switch off fob ${fob.number}?` : 'Switch off your fob?'}
                actions={
                    <>
                        <button type="button" className="btn btn-outline-light" onClick={lost.close}>Keep it on</button>
                        <button type="button" className="btn acc-btn-danger" onClick={reportLost}>Switch off fob</button>
                    </>
                }
            >
                <p>The fob stops opening every door in the building straight away, so whoever finds it cannot use it.</p>
                <ul className="acc-consequences">
                    <li><Icon name="phone" size={18} /><span><strong>Your mobile key keeps working</strong>, so you can still get in.</span></li>
                    <li><Icon name="key" size={18} /><span><strong>A replacement fob</strong> will be waiting at the front desk. Bring a photo ID.</span></li>
                </ul>
            </Modal>
        </div>
    );
}

export default DoorAccessScreen;
